import { leadStateText, type Term, type Tone } from "@/lib/campaign";
import { DEFAULT_CALLBACK_SECONDS, DEFAULT_HOLD_SECONDS } from "@/lib/dispositions";
import { humanize } from "@/lib/lead-card";
import { catalogRow, groupText, stageCatalog } from "@/lib/stages";
import type {
  ContactRow, ContactSummary, Disposition, LeadCard, LeadNote, LeadStageChange, LeadTimelineEvent,
} from "@/lib/types";
import { formatDateTime } from "@/lib/utils";

/**
 * The contacts list and the record (ADR-044 in `platform`), in words: the stages and their three
 * groups (ADR-046), the Dial column's vocabulary on top of the dialer's own, the built-in views as
 * filter sets, the URL as the one place a filter lives, and what a timeline entry or an outcome
 * says. Pure, for `test/contacts.test.ts`.
 *
 * The stages are the tenant's own catalog since ADR-077 (`lib/stages.ts`): every helper here reads
 * it first, and the built-in thirteen below answer until it has loaded, or for a name it no longer
 * has (a deleted stage stays in old history rows).
 *
 * Two facts sit side by side on every row and are never merged: the **stage** is the person's, moved
 * by what happened (`lifecycle.py`); the **dial state** is one campaign's list row, the dialer's.
 */

// --- the stages ------------------------------------------------------------------------------------

export const WORKING_STAGES = [
  "new", "no_response", "chase", "callback_missed", "consult_no_show", "about_to_sign",
] as const;
export const SCHEDULED_STAGES = ["callback_scheduled", "consult_scheduled"] as const;
export const EXIT_STAGES = ["signed_pending_intake", "signed", "not_qualified", "dnc", "mia"] as const;
export const STAGES = [...WORKING_STAGES, ...SCHEDULED_STAGES, ...EXIT_STAGES] as const;
export type Stage = (typeof STAGES)[number];
export type StageGroup = "working" | "scheduled" | "exit";

const STAGE: Record<Stage, Term> = {
  new: { label: "New", description: "Arrived; no outcome yet." },
  no_response: { label: "No response", description: "Called, not reached. The dialer keeps trying." },
  chase: { label: "Chase", description: "Reached and interested; being chased for the next step." },
  callback_missed: { label: "Callback missed", description: "A promised callback that did not connect." },
  consult_no_show: { label: "Consult no-show", description: "Missed the contract review." },
  about_to_sign: { label: "About to sign", description: "The contract is out; waiting for the signature." },
  callback_scheduled: { label: "Callback scheduled", description: "A callback is booked. Not dialed before it." },
  consult_scheduled: { label: "Consult scheduled", description: "A contract review is booked. Not dialed before it." },
  signed_pending_intake: { label: "Signed, pending intake", description: "Signed; the intake is not finished." },
  signed: { label: "Signed", description: "A client. Never dialed again, for any firm." },
  not_qualified: { label: "Not qualified", description: "Not a case for this firm. Off its lists." },
  dnc: { label: "Do not call", description: "Asked not to be called. Every record with the phone." },
  mia: { label: "MIA", description: "Went quiet for good. Off this firm's lists." },
};

const GROUP_TONE: Record<StageGroup, Tone> = { working: "accent", scheduled: "warning", exit: "neutral" };

function builtInGroup(stage: string | null): StageGroup | null {
  if ((WORKING_STAGES as readonly string[]).includes(stage ?? "")) return "working";
  if ((SCHEDULED_STAGES as readonly string[]).includes(stage ?? "")) return "scheduled";
  if ((EXIT_STAGES as readonly string[]).includes(stage ?? "")) return "exit";
  return null;
}

export function stageGroup(stage: string | null): StageGroup | null {
  return catalogRow(stage)?.group ?? builtInGroup(stage);
}

/** A stage in a rep's words. A lead older than the lifecycle has none; a stage this console does not
 *  know is shown as itself. */
export function stageText(stage: string | null): Term & { tone: Tone } {
  if (!stage) {
    return { label: "No stage", description: "Older than the lifecycle: no outcome has moved it yet.", tone: "outline" };
  }
  const tone = (group: StageGroup): Tone => (stage === "dnc" ? "danger" : GROUP_TONE[group]);
  const row = catalogRow(stage);
  if (row) {
    return { label: row.label, description: row.description || groupText(row.group).description, tone: tone(row.group) };
  }
  const known = STAGE[stage as Stage];
  const group = builtInGroup(stage);
  if (!known || !group) return { label: humanize(stage) ?? stage, description: "Not a stage this console knows.", tone: "outline" };
  return { ...known, tone: tone(group) };
}

/** Every stage, in the order the console lists them: the tenant's catalog, or the built-in thirteen. */
export function stageSlugs(): string[] {
  return stageCatalog()?.map((s) => s.slug) ?? [...STAGES];
}

/** Whether `stage` is one of the tenant's stages now: a picker keeps a value it no longer offers. */
export function isCurrentStage(stage: string | null | undefined): boolean {
  return Boolean(stage) && stageSlugs().includes(stage!);
}

/** An exit's reach (rule 3 of ADR-046): every record with the phone, or only the same firm's.
 *  Null for a stage that is not an exit. */
export function exitReach(stage: string | null): "all_firms" | "same_firm" | null {
  const row = catalogRow(stage);
  if (row) return row.group === "exit" ? row.exit_scope ?? "same_firm" : null;
  if (stage === "not_qualified" || stage === "mia") return "same_firm";
  return builtInGroup(stage) === "exit" ? "all_firms" : null;
}

/** Where an admin may reopen a lead to (ADR-076): any working or scheduled stage, never an exit. */
export const REOPEN_STAGES: readonly Stage[] = [...WORKING_STAGES, ...SCHEDULED_STAGES];

/** The same, from the tenant's catalog once it has loaded. */
export function reopenStages(): string[] {
  const catalog = stageCatalog();
  return catalog ? catalog.filter((s) => s.group !== "exit").map((s) => s.slug) : [...REOPEN_STAGES];
}

/** What a reopen did, in a sentence for the toast. */
export function reopenSummary(result: { stage: string; reopened: string[]; lists_restored: number }): string {
  const lists = result.lists_restored === 0
    ? "It was on no dial list the exit had taken it off."
    : `Back on ${result.lists_restored} dial list${result.lists_restored === 1 ? "" : "s"}, where each left off.`;
  const others = result.reopened.length - 1;
  const siblings = others > 0
    ? ` ${others} contact${others === 1 ? "" : "s"} with the same phone came back with it.`
    : "";
  return `Now ${stageText(result.stage).label}. ${lists}${siblings}`;
}

/** The tiles across the top of the list: the working stages and the win, each with its "due now". */
export const STRIP_STAGES: readonly Stage[] = [...WORKING_STAGES, "signed_pending_intake"];

/** The same, from the tenant's catalog once it has loaded: its working stages, then the win. */
export function stripStages(): string[] {
  const catalog = stageCatalog();
  if (!catalog) return [...STRIP_STAGES];
  return [...catalog.filter((s) => s.group === "working").map((s) => s.slug), "signed_pending_intake"];
}

// --- the Dial column ---------------------------------------------------------------------------------

const DIAL_EXTRA: Record<string, Term & { tone: Tone }> = {
  held: {
    label: "Held",
    description: "Kept off the list until the hold passes: a booking, a hold outcome, or the firm paused.",
    tone: "warning",
  },
  on_call: { label: "On a call", description: "Offered to a rep, or a call to the lead is up.", tone: "accent" },
  do_not_call: { label: "Do not call", description: "Nothing dials a do-not-call lead.", tone: "danger" },
};

/** What the Dial column says: core-api's word when it has one, else the dialer's own state. */
export function dialText(dial: string | null): Term & { tone: Tone } {
  if (!dial) return { label: "Not on a list", description: "On no campaign's dial list.", tone: "outline" };
  return DIAL_EXTRA[dial] ?? leadStateText(dial);
}

const COUNTS_ATTEMPTS: ReadonlySet<string> = new Set(["fresh", "retrying", "held", "on_call", "needs_disposition"]);

const HOLD_REASON: Record<string, string> = {
  "firm:paused": "firm paused",
  "firm:resumed": "firm resumed",
};

/** "4 of 14" under a state that is still counting, or why a row is held. */
export function dialDetail(row: Pick<ContactRow, "dial" | "membership">): string | null {
  const m = row.membership;
  if (!m) return null;
  if (row.dial === "held" && m.hold_reason) {
    return HOLD_REASON[m.hold_reason] ?? humanize(m.hold_reason) ?? m.hold_reason;
  }
  if (row.dial && COUNTS_ATTEMPTS.has(row.dial) && m.max_attempts) return `${m.attempts} of ${m.max_attempts}`;
  return null;
}

/** "in 25 min", "in 4 h", "in 2 d 3 h" — or the date, once it is more than a week off. */
export function dueIn(iso: string, now: number = Date.now()): string {
  const seconds = Math.round((Date.parse(iso) - now) / 1000);
  if (!Number.isFinite(seconds) || seconds <= 0) return "now";
  if (seconds > 7 * 86_400) return `on ${formatDateTime(iso)}`;
  const days = Math.floor(seconds / 86_400);
  const hours = Math.floor((seconds % 86_400) / 3600);
  const minutes = Math.round((seconds % 3600) / 60);
  if (days > 0) return hours > 0 ? `in ${days} d ${hours} h` : `in ${days} d`;
  if (hours > 0) return minutes > 0 && minutes < 60 ? `in ${hours} h ${minutes} min` : `in ${hours} h`;
  return `in ${Math.max(1, minutes)} min`;
}

/** The Next column: when the dialer takes the row again, in words; null when it never will. */
export function nextText(row: Pick<ContactRow, "dial" | "next_at">, now: number = Date.now()): string | null {
  if (row.dial === "held") return row.next_at ? `Held until ${formatDateTime(row.next_at)}` : "Held";
  if (row.dial === "fresh" || row.dial === "retrying") {
    return row.next_at ? `Due ${dueIn(row.next_at, now)}` : "Due now";
  }
  return null;
}

// --- the filters, and the URL they live in ----------------------------------------------------------

export interface ContactFilters {
  q?: string;
  /** Firm codes, comma-separated; `none` for no firm. */
  firm?: string;
  stage?: string;
  stage_group?: string;
  language?: string;
  state?: string;
  dial_state?: string;
  dnc?: boolean;
  member?: boolean;
  source?: string;
  /** RFC 3339, or `today` — resolved to local midnight when the request is made, so a link stays "today". */
  created_from?: string;
  created_to?: string;
  sort?: string;
  order?: string;
}

export const FILTER_KEYS = [
  "q", "firm", "stage", "stage_group", "language", "state", "dial_state", "dnc", "member", "source",
  "created_from", "created_to", "sort", "order",
] as const;
type FilterKey = (typeof FILTER_KEYS)[number];
const BOOLEAN_KEYS: ReadonlySet<FilterKey> = new Set(["dnc", "member"]);

export const DEFAULT_SORT = "created";
export const DEFAULT_ORDER = "desc";
export const SORTS: Record<string, string> = {
  created: "Created",
  name: "Name",
  stage: "Stage",
  phone: "Phone",
};

export function filtersFromParams(params: URLSearchParams): ContactFilters {
  const out: Record<string, string | boolean> = {};
  for (const key of FILTER_KEYS) {
    const raw = params.get(key);
    if (raw === null || raw === "") continue;
    if (BOOLEAN_KEYS.has(key)) {
      if (raw === "true" || raw === "false") out[key] = raw === "true";
    } else {
      out[key] = raw;
    }
  }
  return out as ContactFilters;
}

export function paramsFromFilters(filters: ContactFilters): URLSearchParams {
  const params = new URLSearchParams();
  for (const key of FILTER_KEYS) {
    const value = filters[key];
    if (value === undefined || value === "") continue;
    if (key === "sort" && value === DEFAULT_SORT) continue;
    if (key === "order" && value === DEFAULT_ORDER) continue;
    params.set(key, String(value));
  }
  return params;
}

const NARROWING: readonly FilterKey[] = FILTER_KEYS.filter((k) => k !== "sort" && k !== "order");

/** Whether anything narrows the list. Sorting does not. */
export function isFiltered(filters: ContactFilters): boolean {
  return NARROWING.some((key) => filters[key] !== undefined && filters[key] !== "");
}

/** Local midnight, as RFC 3339: "today" as a floor manager means it, not "24 hours ago". */
export function localMidnight(now: Date = new Date()): string {
  const midnight = new Date(now);
  midnight.setHours(0, 0, 0, 0);
  return midnight.toISOString();
}

/** The filters as the API takes them: `today` resolved, the defaults left out. */
export function apiFilters(filters: ContactFilters, now: Date = new Date()): Record<string, string | boolean | undefined> {
  const out: Record<string, string | boolean | undefined> = { ...filters };
  if (out.created_from === "today") out.created_from = localMidnight(now);
  if (out.sort === DEFAULT_SORT) delete out.sort;
  if (out.order === DEFAULT_ORDER) delete out.order;
  return out;
}

// --- the built-in views -----------------------------------------------------------------------------

export interface ContactView {
  key: string;
  label: string;
  filters: ContactFilters;
  count: (summary: ContactSummary) => number;
}

const groupCount = (summary: ContactSummary, group: StageGroup) =>
  summary.by_stage.filter((s) => stageGroup(s.stage) === group).reduce((n, s) => n + s.count, 0);

/** The chips under the toolbar. Each is a filter set, so each is a link. */
export const VIEWS: readonly ContactView[] = [
  { key: "all", label: "All", filters: {}, count: (s) => s.total },
  { key: "new_today", label: "New today", filters: { created_from: "today" }, count: (s) => s.new_today },
  { key: "working", label: "Working", filters: { stage_group: "working" }, count: (s) => groupCount(s, "working") },
  { key: "booked", label: "Booked", filters: { stage_group: "scheduled" }, count: (s) => groupCount(s, "scheduled") },
  { key: "exits", label: "Exits", filters: { stage_group: "exit" }, count: (s) => groupCount(s, "exit") },
  { key: "needs_firm", label: "Needs firm", filters: { firm: "none" }, count: (s) => s.needs_firm },
  { key: "unassigned", label: "Unassigned", filters: { member: false }, count: (s) => s.unassigned },
  { key: "exhausted", label: "Exhausted", filters: { dial_state: "exhausted" }, count: (s) => s.exhausted },
  { key: "dnc", label: "Do not call", filters: { dnc: true }, count: (s) => s.dnc },
];

/** The keys a view sets. A view is lit when these match it exactly; the search, the sort and the
 *  other selects do not unlight one. */
const VIEW_KEYS: readonly FilterKey[] = ["stage_group", "stage", "firm", "member", "dial_state", "dnc", "created_from"];

export function activeView(filters: ContactFilters): string | null {
  const view = VIEWS.find((v) => VIEW_KEYS.every((key) => filters[key] === v.filters[key]));
  return view?.key ?? null;
}

/** The filters with this view applied: its keys replaced, everything else kept. */
export function applyView(filters: ContactFilters, view: ContactView): ContactFilters {
  const out: ContactFilters = { ...filters };
  for (const key of VIEW_KEYS) delete out[key];
  return { ...out, ...view.filters };
}

/** The filters with one stage tile lit — or none, clicking the lit one. */
export function toggleStage(filters: ContactFilters, stage: string): ContactFilters {
  const out: ContactFilters = { ...filters };
  delete out.stage_group;
  if (out.stage === stage) delete out.stage;
  else out.stage = stage;
  return out;
}

// --- the record --------------------------------------------------------------------------------------

export function contactName(lead: { first_name: string | null; last_name: string | null }): string | null {
  return [lead.first_name, lead.last_name].filter(Boolean).join(" ").trim() || null;
}

/** "Since 20 min ago" is `relativeTime`; this is the sentence under the header. */
export function stageLine(card: Pick<LeadCard, "stage" | "stage_since">, since: string): string {
  const text = stageText(card.stage).label;
  return card.stage_since ? `${text} · since ${since}` : text;
}

const REASON_WORDS: [RegExp, string][] = [
  [/^created$/, "when the record was created"],
  [/^disposition:/, "from the outcome"],
  [/^trigger:/, "by a trigger"],
  [/^sibling:/, "from the other record with this phone"],
  [/^arrival:dnc_sibling$/, "a record with this phone is do-not-call"],
  [/^import:/, "by the import"],
  [/^console:/, "typed into the console"],
  [/^reopen/, "reopened by an admin"],
  [/^stage_removed:/, "its stage was deleted"],
];

/** A stage move: "No response → Callback missed", and why. */
export function stageChangeText(change: LeadStageChange): { title: string; detail: string | null } {
  const to = stageText(change.to_stage).label;
  const title = change.from_stage ? `${stageText(change.from_stage).label} → ${to}` : to;
  const detail = REASON_WORDS.find(([re]) => re.test(change.reason))?.[1] ?? null;
  return { title, detail };
}

function text(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value : null;
}

/** An event-log row on the timeline, in words. Only the types core-api puts there. */
export function systemEventText(event: LeadTimelineEvent): string {
  const p = event.payload;
  switch (event.type) {
    case "lead.created":
      return `Created${text(p.source) ? ` from ${text(p.source)}` : ""}`;
    case "compliance.blocked":
      return `Marked do-not-call${text(p.detail) ? ` · ${text(p.detail)}` : ""}`;
    case "disposition.set":
      return `Outcome ${humanize(text(p.disposition)) ?? "set"} by ${text(p.by) ?? "the system"}`;
    case "appointment.booked":
      return "Appointment booked";
    case "appointment.no_show":
      return "Appointment missed";
    case "call.rep_connected":
      return "Direct call answered by a rep";
    case "call.missed":
      return `Missed direct call${text(p.reason) ? ` · ${humanize(text(p.reason))}` : ""}`;
    default:
      return humanize(event.type.replaceAll(".", " ")) ?? event.type;
  }
}

/** What a note is: typed on the record, typed with an outcome, the handoff's brief, or the platform's. */
export function noteTitle(note: Pick<LeadNote, "kind" | "disposition">, label: (slug: string) => string | undefined): string {
  switch (note.kind) {
    case "disposition":
      return note.disposition ? `Note with ${label(note.disposition) ?? humanize(note.disposition)}` : "Note with the outcome";
    case "handoff":
      return "Handoff brief";
    case "system":
      return "System";
    default:
      return "Note";
  }
}

// --- an outcome off a call --------------------------------------------------------------------------

/** "4 h", "30 min", "2 d": a backoff or a hold as a person says it. */
export function spanWords(seconds: number): string {
  if (seconds >= 86_400) return `${Math.round(seconds / 86_400)} d`;
  if (seconds >= 3600) return `${Math.round(seconds / 3600)} h`;
  return `${Math.max(1, Math.round(seconds / 60))} min`;
}

/** The one-line effect under an outcome's button: what choosing it does to the lead. The server
 *  decides all of it (`lifecycle.py`, the dialer's recycling); this says it before the click.
 *
 *  An outcome that ends the lead and names a campaign (MIA and the MIA campaign) leaves them on
 *  that campaign's list and no other. `handsTo` is the campaign's name, where the reader may
 *  know it: a rep cannot list campaigns. */
export function outcomeEffect(
  d: Pick<Disposition, "kind" | "category" | "backoff_seconds" | "hold_seconds" | "stage"> &
    Partial<Pick<Disposition, "enlist_campaign_id">>,
  handsTo?: string | null,
): string {
  if (d.category === "compliance") return "Every record with this phone · an admin cannot undo it";
  const stage = d.stage ?? null;
  const reach = exitReach(stage);
  if (reach && d.enlist_campaign_id) return `Handed to ${handsTo ?? "its campaign"} · leaves every other list`;
  if (reach === "all_firms") return "Leaves every list · the same phone too";
  if (stage === "not_qualified" && reach) return "Asks for the reason · leaves this firm's lists";
  if (reach) return "Leaves this firm's lists";
  if (d.kind === "hold") {
    // The dialer's own default when the row names no span (`recycling.py`).
    return `Held for ${spanWords(d.hold_seconds ?? d.backoff_seconds ?? DEFAULT_HOLD_SECONDS)}`;
  }
  if (d.kind === "terminal") return stage ? `${stageText(stage).label} · done on this list` : "Done on this list";
  const back = d.backoff_seconds ? ` · due in ${spanWords(d.backoff_seconds)}` : "";
  if (d.kind === "callback") return `Callback for you · due in ${spanWords(d.backoff_seconds ?? DEFAULT_CALLBACK_SECONDS)}`;
  if (!stage) return "Label only · nothing moves";
  return `${stageText(stage).label}${back}`;
}

/** What the toast says after an outcome is saved off a call: the callback's time, the stage, and
 *  the campaign the lead was handed to. */
export function outcomeSaved(
  result: { stage: string | null; stage_moved: boolean; handed_to_campaign?: string | null },
  when: string | null,
): string {
  const stage = stageText(result.stage).label;
  const parts = [
    when ? `Call back ${when}.` : null,
    result.stage_moved ? `Stage: ${stage}.` : `The stage stays ${stage}.`,
    result.handed_to_campaign ? `On ${result.handed_to_campaign}'s list now.` : null,
  ];
  return parts.filter(Boolean).join(" ");
}

/** Why a working outcome is off on a lead that has left: the exit is protected (ADR-046). */
export function exitProtected(
  card: Pick<LeadCard, "stage" | "stage_since">,
  d: Pick<Disposition, "category" | "stage">,
): string | null {
  if (stageGroup(card.stage) !== "exit") return null;
  if (d.category === "compliance") return null;
  const target = d.stage ?? null;
  if (!target || stageGroup(target) === "exit") return null;
  const when = card.stage_since ? ` on ${formatDateTime(card.stage_since).split(",").slice(0, 2).join(",")}` : "";
  return `${stageText(card.stage).label}${when} — an admin can reopen`;
}
